import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    CircularProgress,
    Box,
    Typography,
    Breadcrumbs,
    Link
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import axios from 'axios';

export default function ImportFromDocumentsDialog({ open, woid, onSelect, onCancel }) {
    const [loading, setLoading] = useState(false);
    const [documents, setDocuments] = useState([]);
    const [currentFolder, setCurrentFolder] = useState(null);
    const [breadcrumbs, setBreadcrumbs] = useState([]);
    const [selectedDocs, setSelectedDocs] = useState([]);

    useEffect(() => {
        if (open && woid) {
            fetchDocuments();
        }
    }, [open, woid]);

    useEffect(() => {
        if (!open) {
            setCurrentFolder(null);
            setBreadcrumbs([]);
            setSelectedDocs([]);
        }
    }, [open]);

    const fetchDocuments = async () => {
        setLoading(true);
        try {
            const response = await axios.post('api/documentswo', { woid });
            const data = JSON.parse(response.data.jsonString);
            if (data.status === 'success' && data.contents) {
                setDocuments(data.contents);
                setBreadcrumbs([{ name: 'Documents', items: data.contents }]);
            }
        } catch (error) {
            console.error('Failed to fetch documents:', error);
        } finally {
            setLoading(false);
        }
    };

    const getCurrentItems = () => {
        if (currentFolder && currentFolder.SubItems) {
            return currentFolder.SubItems;
        }
        return documents;
    };

    const handleFolderClick = (folder) => {
        setCurrentFolder(folder);
        setBreadcrumbs([...breadcrumbs, { name: folder.Name, items: folder.SubItems || [] }]);
    };

    const handleBreadcrumbClick = (index) => {
        if (index === 0) {
            setCurrentFolder(null);
            setBreadcrumbs([breadcrumbs[0]]);
        } else {
            const newBreadcrumbs = breadcrumbs.slice(0, index + 1);
            setBreadcrumbs(newBreadcrumbs);
            // Find the folder from the parent's items
            const parentItems = newBreadcrumbs[index - 1]?.items || documents;
            const folder = parentItems.find(item => item.Name === newBreadcrumbs[index].name);
            setCurrentFolder(folder || null);
        }
    };

    const handleFileClick = (file) => {
        setSelectedDocs(prev => {
            const isSelected = prev.some(d => d.id === file.Id);
            if (isSelected) {
                // Deselect
                return prev.filter(d => d.id !== file.Id);
            } else {
                // Add to selection
                return [...prev, { id: file.Id, name: file.Name }];
            }
        });
    };

    const handleImport = () => {
        if (selectedDocs.length > 0) {
            onSelect(selectedDocs);
        }
    };

    const getFileIcon = (filename) => {
        const ext = filename?.split('.').pop()?.toLowerCase();
        if (ext === 'pdf') return <PictureAsPdfIcon />;
        if (['jpg', 'jpeg', 'png', 'gif', 'bmp'].includes(ext)) return <ImageIcon />;
        return <InsertDriveFileIcon />;
    };

    const isImportable = (filename) => {
        const ext = filename?.split('.').pop()?.toLowerCase();
        // PSPDFKit can import: PDF, images, Office docs
        return ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'bmp', 'tiff', 'docx', 'doc', 'xlsx', 'xls', 'pptx', 'ppt'].includes(ext);
    };

    const items = getCurrentItems();

    return (
        <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
            <DialogTitle>Import from Work Order Documents</DialogTitle>
            <DialogContent>
                {loading ? (
                    <Box display="flex" justifyContent="center" p={4}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <Breadcrumbs aria-label="breadcrumb" style={{ marginBottom: 16 }}>
                            {breadcrumbs.map((crumb, index) => (
                                index === breadcrumbs.length - 1 ? (
                                    <Typography key={index} color="textPrimary">
                                        {crumb.name}
                                    </Typography>
                                ) : (
                                    <Link
                                        key={index}
                                        color="inherit"
                                        href="#"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handleBreadcrumbClick(index);
                                        }}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        {crumb.name}
                                    </Link>
                                )
                            ))}
                        </Breadcrumbs>

                        {items && items.length > 0 ? (
                            <List style={{ maxHeight: 400, overflow: 'auto' }}>
                                {items.map((item) => (
                                    <ListItem
                                        key={item.Id}
                                        button
                                        selected={selectedDocs.some(d => d.id === item.Id)}
                                        onClick={() => item.IsFolder ? handleFolderClick(item) : handleFileClick(item)}
                                        disabled={!item.IsFolder && !isImportable(item.Name)}
                                    >
                                        <ListItemIcon>
                                            {item.IsFolder ? <FolderIcon /> : getFileIcon(item.Name)}
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={item.Name}
                                            secondary={item.IsFolder ? `${item.SubItems?.length || 0} items` : null}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        ) : (
                            <Typography color="textSecondary" align="center" style={{ padding: 32 }}>
                                No documents found
                            </Typography>
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel}>Cancel</Button>
                <Button
                    onClick={handleImport}
                    color="primary"
                    variant="contained"
                    disabled={selectedDocs.length === 0}
                >
                    Import{selectedDocs.length > 0 ? ` (${selectedDocs.length})` : ''}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
